import React from 'react';
import { StyleSheet, Platform, TextStyle, ViewStyle, StyleProp } from 'react-native';
import { TextInput } from '@/components/common/text';
import { APP_CONSTANTS } from '@/constants/residents';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

type Props = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  inputRef?: React.RefObject<TextInput | null>;
  style?: StyleProp<TextStyle | ViewStyle>;
  editable?: boolean;
  onSubmitEditing?: () => void;
};

export const EmailInput: React.FC<Props> = ({
  value,
  onChangeText,
  placeholder = 'Email',
  autoFocus,
  inputRef,
  style,
  editable = true,
  onSubmitEditing,
}) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();

  return (
    <TextInput
      ref={inputRef}
      style={[styles.input, style as any]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      // muted is the right weight for a placeholder — clearly lighter than typed
      // text. It only became legible in dark once background.input was darkened
      // in this same change; it now measures about 4.5 in light and 4.7 in dark.
      placeholderTextColor={colors.text.muted}
      keyboardType="email-address"
      autoCapitalize="none"
      autoCorrect={false}
      textContentType={Platform.select({ ios: 'username', default: 'emailAddress' }) as any}
      autoComplete="email"
      autoFocus={autoFocus}
      editable={editable}
      onSubmitEditing={onSubmitEditing}
    />
  );
};

// Mapping mirrors the already-themed sibling at components/auth/PasswordInput,
// so the two auth input styles converge instead of drifting. Note the fill and
// the border used to be the same flat grey; they now separate the way that
// sibling does, which is a deliberate visual change in light mode.
const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  input: {
    minHeight: APP_CONSTANTS.TEXT_FIELD_HEIGHT,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS,
    paddingHorizontal: 16,
    fontSize: APP_CONSTANTS.INPUT_FONT_SIZE,
    color: colors.text.primary,
    backgroundColor: semantic.background.input,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
});


